<script setup lang="ts">
import { inject, ref, onMounted } from 'vue'
import { useToast as usePrimeToast } from 'primevue/usetoast'
import { addPage } from '~/layers/cms/app/composables/cms'
import { default_page_settings, pageGroupOptions, type PageSettings } from '~/layers/cms/app/types/cmsTypes'
import { isSlugValid, normalizeSlug, useChooseFile } from '#imports'
import type Select from 'primevue/select'

const toast = usePrimeToast()
const dialogRef = inject<DialogRef>('dialogRef')

interface DialogRef {
  value: {
    data: {
      defaultGroup: string
    }
    close(): void
  }
}

const closeDialog = () => {
  if (!dialogRef) return
  dialogRef.value.close()
}

onMounted(() => {
  if (!dialogRef) return

  if (dialogRef.value?.data?.defaultGroup) {
    new_page.value.group = dialogRef.value.data.defaultGroup
  }
})

const addNewPage = async () => {
  if (!dialogRef) return

  const normalized = normalizeSlug(new_page.value.slug)
  new_page.value.slug = normalized

  if (!normalized) return

  if (!isSlugValid(normalized)) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Niepoprawny slug strony',
      life: 3000
    })
    return
  }

  if (!new_page.value.metaTitle) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Tytuł strony jest wymagany',
      life: 3000
    })
    return
  }

  const response = await addPage(new_page.value)

  if (!response.status_ok) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Nie udało się dodać strony ' + response.message,
      life: 3000
    })
  } else {
    toast.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Poprawnie dodano usera',
      life: 3000
    })
    closeDialog()
  }
}

const new_page = ref<PageSettings>(default_page_settings)

const { chooseFile } = useChooseFile()
const selectFile = async (assign: (src: string) => void) => {
  const newSrc = await chooseFile()
  if (newSrc) assign(newSrc)
}

const updateSlugFromTitle = () => {
  const normalized = normalizeSlug(new_page.value.metaTitle)
  if (isSlugValid(normalized)) {
    new_page.value.slug = normalized
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <span class="text-xs font-medium uppercase tracking-wide text-gray-400">SEO</span>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Meta title*</label>
        <InputText
          v-model="new_page.metaTitle"
          class="flex-1 text-sm"
          @change="updateSlugFromTitle"
          @input="updateSlugFromTitle"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Slug*</label>
        <InputText
          v-model="new_page.slug"
          class="flex-1 text-sm"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Meta description</label>
        <InputText
          v-model="new_page.metaDescription"
          class="flex-1 text-sm"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Indeksowanie</label>
        <ToggleSwitch v-model="new_page.indexPage" />
        <span class="text-gray-500 text-xs">Indeksuj stronę</span>
      </div>

      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Favico</label>
        <div class="flex flex-col gap-2">
          <div
            v-for="item in [
              { key: 'faviconIco', label: 'favicon.ico' },
              { key: 'favico16', label: '16×16' },
              { key: 'favico32', label: '32×32' },
              { key: 'favico48', label: '48×48' },
              { key: 'favico64', label: '64×64' },
              { key: 'appleTouchIcon', label: 'Apple 180×180' },
              { key: 'favico192', label: 'Android 192×192' },
              { key: 'favico512', label: 'PWA 512×512' }
            ]"
            :key="JSON.stringify(item)"
            class="p-2 flex flex-row items-center justify-between gap-2"
          >
            <div class="flex items-center gap-2 min-w-0">
              <template v-if="new_page[item.key]">
                <NuxtImg
                  :src="new_page[item.key]"
                  class="w-6 h-6"
                  alt="icon"
                  width="24"
                  height="24"
                  loading="lazy"
                  quality="100"
                />
              </template>

              <div class="flex flex-col min-w-0">
                <span>{{ item.label }}</span>
                <span
                  class="text-xs"
                  :class="new_page[item.key] ? 'text-green-600' : 'text-gray-500'"
                >
                  {{ new_page[item.key] ? 'Ustawiono' : 'Brak' }}
                </span>
              </div>
            </div>

            <button
              class="button-admin-blue-transparent"
              @click="selectFile(src => new_page![item.key] = src)"
            >
              {{ new_page[item.key] ? 'Zmień' : 'Dodaj' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-1">
      <span class="text-xs font-medium uppercase tracking-wide text-gray-400">Open Graph</span>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">OG title</label>
        <InputText
          v-model="new_page.ogTitle"
          class="flex-1 text-sm"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">OG description</label>
        <InputText
          v-model="new_page.ogDescription"
          class="flex-1 text-sm"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">OG image</label>
        <div
          class="w-12 h-12 border border-dashed border-gray-800 rounded flex items-center justify-center cursor-pointer overflow-hidden bg-gray-700"
          @click="selectFile(src => new_page!.ogImage = src)"
        >
          <NuxtImg
            v-if="new_page.ogImage"
            :src="new_page.ogImage"
            class="w-full h-full object-cover"
            loading="lazy"
            width="400"
            height="400"
            format="webp"
            alt="ogImage"
            quality="100"
          />
          <span
            v-else
            class="text-gray-800 text-xl"
          >+</span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Twitter card</label>
        <InputText
          v-model="new_page.twitterCard"
          class="flex-1 text-sm"
        />
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <span class="text-xs font-medium uppercase tracking-wide text-gray-400">Zaawansowane</span>
      <div class="flex items-start gap-3">
        <label class="w-32 shrink-0 text-gray-500 pt-1">Head code</label>
        <Textarea
          v-model="new_page.customHeadCode"
          class="flex-1 h-16 min-h-16 text-sm resize-y"
        />
      </div>
      <div class="flex items-start gap-3">
        <label class="w-32 shrink-0 text-gray-500 pt-1">Body code</label>
        <Textarea
          v-model="new_page.customBodyCode"
          class="flex-1 h-16 min-h-16 text-sm resize-y"
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="w-32 shrink-0 text-gray-500">Grupa</label>
        <Select
          v-model="new_page.group"
          :options="pageGroupOptions"
          class="flex-1 px-2 text-sm"
        />
      </div>
      <div
        v-if="new_page.group == 'aktualność'"
        class="flex items-center gap-3"
      >
        <label class="w-32 shrink-0 text-gray-500">Tytuł aktualności</label>
        <InputText
          v-model="(new_page.customData.articleTitle as string)"
          class="flex-1 text-sm"
        />
      </div>
      <div
        v-if="new_page.group == 'aktualność'"
        class="flex items-center gap-3"
      >
        <label class="w-32 shrink-0 text-gray-500">Opis aktualności</label>
        <InputText
          v-model="(new_page.customData.articleDescription as string)"
          class="flex-1 text-sm"
        />
      </div>
      <div
        v-if="new_page.group == 'aktualność'"
        class="flex items-center gap-3"
      >
        <label class="w-32 shrink-0 text-gray-500">Data aktualności</label>
        <DatePicker
          v-model="(new_page.customData.articleDate as Date)"
          date-format="dd-mm-yy"
          show-icon
          show-button-bar
          :manual-input="true"
          placeholder="DD-MM-RRRR"
          class="flex-1 text-sm"
          :block-scroll="true"
          modal
        />
      </div>
      <div
        v-if="new_page.group == 'aktualność'"
        class="flex items-center gap-3"
      >
        <label class="w-32 shrink-0 text-gray-500">Grafika aktualności</label>
        <NuxtImg
          :src="(new_page.customData.articleImage as string)"
          width="400"
          height="200"
          class="object-cover w-100 h-50 min-w-100 min-h-50 cursor-pointer"
          loading="lazy"
          format="webp"
          alt="grafika aktualności"
          quality="100"
          @click="selectFile(src => { new_page!.customData ??= {}; new_page!.customData.articleImage = src })"
        />
      </div>
    </div>
    <Button
      type="button"
      label="Dodaj"
      severity="primary"
      @click="addNewPage"
    />
  </div>
</template>
